import { MdOutlinePersonAddDisabled } from "react-icons/md";
import { FILE_STORAGE_SERVER_URL } from "src/helpers/constants";
import { memo, useId, useMemo, useState } from "react";
import {
  getCountryDataByCode,
  getShortName,
  showNotification,
} from "src/helpers/utils";
import useMobile from "src/hooks/useMobile";
import useUsers from "src/hooks/useUsers";
import NetworkProfile from "../NetworkProfile/NetworkProfile";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import useCache from "src/hooks/useCache";
import { useTranslation } from "react-i18next";
import useInvites from "src/hooks/useInvites";
import { EPrivacyVisibility } from "../CurrentUserPrivacyModal/CurrentUserPrivacyModal";
import ContactCard, { EContactCardType } from "./ContactCard";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IAcceptedContactInviteCard {
  id: string;
  user: string;
  animationIndex: number;
}

const AcceptedContactInviteCard: React.FC<IAcceptedContactInviteCard> = ({
  id,
  user,
  animationIndex,
}) => {
  const { isMobile } = useMobile();
  const { t } = useTranslation();
  const { clearCache } = useCache();
  const { getUserById } = useUsers();
  const tooltipId = useId();
  const { acceptContactInvite, deleteContactInvite, resetInvitesCache } =
    useInvites();
  const [showUserInfoModal, setShowUserInfoModal] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  const [isInviting, setIsInviting] = useState<boolean>(false);

  const userInfo = useMemo(() => {
    return getUserById({ _id: user });
  }, [user]);

  const privacySettings = useMemo(() => {
    return userInfo?.settings?.privacy || {};
  }, [userInfo]);

  const userImage = useMemo(() => {
    if (
      userInfo?.picture &&
      userInfo?.settings?.privacy?.seePicture === EPrivacyVisibility.EVERYONE
    ) {
      return `${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${userInfo?.picture}`;
    }

    return null;
  }, [userInfo]);

  const seeImage = useMemo(() => {
    return (
      privacySettings?.seePicture === EPrivacyVisibility.EVERYONE ||
      privacySettings?.seePicture === EPrivacyVisibility.CONTACTS
    );
  }, [privacySettings]);

  const seeContact = useMemo(() => {
    return (
      privacySettings?.seeContact === EPrivacyVisibility.EVERYONE ||
      privacySettings?.seeContact === EPrivacyVisibility.CONTACTS
    );
  }, [privacySettings]);

  const seeIntro = useMemo(() => {
    return (
      privacySettings?.seeIntro === EPrivacyVisibility.EVERYONE ||
      privacySettings?.seeIntro === EPrivacyVisibility.CONTACTS
    );
  }, [privacySettings]);

  const isPrivate = useMemo(() => {
    return !seeContact && !seeIntro && !seeImage;
  }, [seeIntro, seeContact, seeImage]);

  const subTextContent = useMemo(() => {
    if (isPrivate) {
      return t("modals:network_profile.private");
    }

    return (
      getCountryDataByCode({ code: userInfo?.country })?.name ||
      t("modals:network_profile.no_country")
    );
  }, [getCountryDataByCode, userInfo]);

  return (
    <>
      <ContactCard
        type={EContactCardType.ACCEPTED}
        actions={[
          {
            onClick: () => setShowDeleteDialog(true),
            icon: <MdOutlinePersonAddDisabled size={ICON_SIZES.MD} />,
            kind: EButtonKind.DANGER,
            tooltip: t("common:buttons.delete"),
            isLoading: isInviting,
          },
        ]}
        index={animationIndex}
        name={userInfo?.name}
        picture={userImage}
        subText={subTextContent}
        onClick={() => setShowUserInfoModal(true)}
      />

      {showUserInfoModal && (
        <NetworkProfile
          profileInfo={userInfo}
          onClose={() => setShowUserInfoModal(false)}
          actions={[
            {
              onClick: () => setShowDeleteDialog(true),
              icon: <MdOutlinePersonAddDisabled size={ICON_SIZES.MD} />,
              kind: EButtonKind.DANGER,
              text: t("common:buttons.delete"),
              isLoading: isInviting,
            },
          ]}
        />
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          description={`¿Deseas eliminar a ${getShortName(
            userInfo?.name,
          )} de tus contactos?`}
          onConfirm={async () => {
            setIsInviting(true);
            const res = await deleteContactInvite(id);
            if (res?.data?.success) {
              showNotification({
                kind: "success",
                message: "Invitación eliminada",
              });
              await resetInvitesCache();
              setIsInviting(false);

              setShowDeleteDialog(false);
              setShowUserInfoModal(false);
            } else {
              setIsInviting(false);

              showNotification({
                kind: "error",
                message: "Error eliminando la invitación",
              });
            }
          }}
          onClose={() => setShowDeleteDialog(false)}
          confirmKind={EButtonKind.DANGER}
          title="Eliminar"
          isLoading={isInviting}
          doubleConfirmRequired
        />
      )}
    </>
  );
};

export default memo(AcceptedContactInviteCard);
